import { gql } from "@apollo/client";
import { ComponentStateTypes } from "components/common/ComponentState";
import Form from "components/common/form/Form";
import DateInput from "components/common/form/inputs/date/DateInput";
import Input from "components/common/form/inputs/Input";
import Textarea from "components/common/form/inputs/Textarea";
import FormActions from "components/common/form/partials/FormActions";
import Page from "components/common/page/Page";
import WorkAgricultureMaintenanceLogFormFields, { WorkAgricultureMaintenanceLogModelType } from "components/entities/agriculture/work/maintenances/WorkAgricultureMaintenanceLogFormFields";
import SeasonDropdown from "components/entities/app/season/inputs/SeasonDropdown";
import WorkersInput, { WorkersFormType } from "components/entities/app/work/WorkersInput";
import { uuid } from "config/app-constants";
import { graph } from "config/axios-context";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AgricultureMaintenanceFormDataDocument, AgricultureMaintenanceFormDataQuery, AgricultureMaintenanceFormDataQueryVariables, MaintenanceAgricultureWorkInput, SaveMaintenanceMutation, SaveMaintenanceMutationVariables } from "types/graphql";
import { PersonType } from "types/person-types";
import { SeasonType } from "types/season-type";
import { DateUtil } from "utilities/date-utilities";
import useAppContext from "utilities/hooks/useAppContext";

const query = gql`
query AgricultureMaintenanceFormData($id: Int!) {
    season(where: {active: {eq: true}}) {
      id
      name
      active
    }
    maintenanceAgricultureWork(id: $id) {
      id
      workers {
        items {
          person {
            id
            name
          }
          price
          id
        }
      }
      season {
        active
        id
        name
      }
      logs {
        items {
            id
          type {
            name
            id
          }
          investment {
            id
            name
            investmentType
            unit
            unitPrice
            total
          }
          maintenanceLogFruitGroups {
            items {
              id
              fruitGroup {
                name
                id
              }
            }
          }
        }
      }
      startDate
      endDate
      note
      name
    }
  }
`;

const mutation = gql`
mutation SaveMaintenance($model: MaintenanceAgricultureWorkInput!) {
    maintenanceAgricultureWork(model: $model) {
      id
    }
}
`;

type MaintenanceAgricultureWorkInputType = {
    season: SeasonType,
    endDate?: Date,
    startDate: Date,
    id: number|string,
    name: string,
    note?: string,
    workers: WorkersFormType[],
    logs: WorkAgricultureMaintenanceLogModelType[]
}

const AgricultureMaintenanceFormPage = () => {
    const params = useParams<{id: string}>();
    const [componentState, setComponentState] = useState<ComponentStateTypes>('loading');
    const context = useAppContext();
    const navigate = useNavigate();
    const [model, setModel] = useState<Partial<MaintenanceAgricultureWorkInputType>>({
        season: undefined,
        endDate: undefined,
        startDate: new Date(),
        id: 0,
        name: '',
        note: '',
        workers: [{
            person: undefined,
            price: '',
            id: uuid()
        }],
        logs: [{
            id: uuid(),
            investment: undefined,
            type: undefined as any,
            maintenanceLogFruitGroups: []
        }]
    });
    
    useEffect(() => {
        loadData().then();
     
    }, []);

    const loadData = async () => {
        const id = params.id;
        try {
            const response = await graph<AgricultureMaintenanceFormDataQuery, AgricultureMaintenanceFormDataQueryVariables>({
                query: AgricultureMaintenanceFormDataDocument,
                variables: {
                    id: id ? parseInt(id) : 0
                }
            });
            
            if(response.errors) {
                context.notify('error', response.errors[0].message);
                throw response.errors;
            }

            let theModel: Partial<MaintenanceAgricultureWorkInputType> = {
                ...model,
                season: response.data?.season as SeasonType,
            }

            if(response.data?.maintenanceAgricultureWork) {
                theModel = {
                    endDate: response.data.maintenanceAgricultureWork.endDate ? new Date(response.data.maintenanceAgricultureWork.endDate) : undefined,
                    startDate: new Date(response.data.maintenanceAgricultureWork.startDate),
                    id: response.data.maintenanceAgricultureWork.id,
                    name: response.data.maintenanceAgricultureWork.name,
                    note: response.data.maintenanceAgricultureWork.note || '',
                    season: response.data.maintenanceAgricultureWork.season as SeasonType,
                    workers: response.data.maintenanceAgricultureWork.workers.items.map(w => ({
                        person: w.person as PersonType,
                        price: w.price,
                        id: w.id
                    })),
                    logs: response.data.maintenanceAgricultureWork.logs.items.map(l => {
                        return {
                            id: l.id,
                            type: l.type,
                            investment: (l.investment || undefined) as any,
                            maintenanceLogFruitGroups: l.maintenanceLogFruitGroups.items.map(f => {
                                return {
                                    id: f.id,
                                    classificationId: f.fruitGroup.id,
                                    name: f.fruitGroup.name
                                }
                            })
                        }
                    })
                }
            }

            setModel(theModel);
            setComponentState('');
        } catch (e) {
            context.notify('error', 'error.loading-data');
            setComponentState('error');
            throw e;
        }
    };

    const insertIntoModel = (key: keyof MaintenanceAgricultureWorkInputType, value: any) => {
        setModel({
            ...model,
            [key]: value
        });
    };

	const onSubmit = async () => {
        const data: MaintenanceAgricultureWorkInput = {
            id: Number(model.id) || 0,
            name: model.name!,
            startDate: model.startDate && DateUtil.toIsoDate(model.startDate),
            endDate: model.endDate && DateUtil.toIsoDate(model.endDate),
            note: model.note,
            seasonId: model.season!.id,
            workers: model.workers!.map(w => ({
                personId: Number(w.person!.id),
                price: Number(w.price),
                id: Number(w.id) || 0,
                date: DateUtil.toIsoDate(model.startDate)
            })),
            logs: model.logs!.map(l => {
                return {
                    id: Number(l.id) || 0,
                    typeId: Number(l.type!.id),
                    investmentId: l.investment ? Number(l.investment.id) : undefined,
                    maintenanceLogFruitGroups: l.maintenanceLogFruitGroups.map(f => {
                        return {
                            id: Number(f.id) || 0,
                            fruitGroupId: Number(f.classificationId),
                            logId: Number(l.id) || 0
                        }
                    }),
                    workId: Number(model.id) || 0
                }
            })
        };

		try {            
            const response = await graph<SaveMaintenanceMutation, SaveMaintenanceMutationVariables>({
                query: mutation,
                variables: {
                    model: data
                }
            });
    
            if(response.errors) {
                throw response.errors;
            }
    
            context.notify('success', 'form.submit.success');
            navigate(-1);
        } catch (error: any) {
            context.notify('error', 'form.submit.error', error.message);
            throw error;
        }
	}

	return (
		<Page title="pages.agriculture.work.maintenance.form" state={componentState}>
			<Form name="agricultureWorkMaintenanceFormPage" onSubmit={onSubmit}>
            <fieldset>
                    <SeasonDropdown
                        value={model.season}
                        required
                        label="fields.season.name"
                        onChange={s => insertIntoModel('season', s)}
                    />
                    <Input
                        name="name"
                        value={model.name}
                        onChange={v => insertIntoModel('name', v)}
                        required
                        maxLength={128}
                        minLength={2}
                        label="fields.name.name"
                    />
                    <DateInput
                        required
                        label="fields.startDate.name"
                        value={model.startDate}
                        name="startDate"
                        onChange={d => insertIntoModel('startDate', d)}
                    />
                    <DateInput
                        label="fields.endDate.name"
                        value={model.endDate}
                        min={model.startDate}
                        disabled={!model.startDate}
                        name="startDate"
                        onChange={d => insertIntoModel('endDate', d)}
                    />
                </fieldset>
                <fieldset>
                    <WorkAgricultureMaintenanceLogFormFields
                        name="agricultureWorkMaintenanceFormPageLogs"
                        value={model.logs}
                        required
                        seasonId={model.season?.id}
                        onChange={v => insertIntoModel('logs', v)}
                    />
                </fieldset>
                <fieldset>
                    <WorkersInput
                        name="agricultureWorkMaintenanceFormPageWorkers"
                        value={model.workers}
                        required
                        onChange={v => insertIntoModel('workers', v)}
                    />
                </fieldset>
                <fieldset>
                    <Textarea label="fields.note.name" name="note" value={model.note} onChange={v => insertIntoModel('note', v)}/>
                </fieldset>
                <FormActions />
			</Form>
		</Page>
	);
};

export default AgricultureMaintenanceFormPage;
